import { TYPE__class_constructor } from '@shared/types/TYPE__class_constructor';
import { FIELD__base } from '../../_fields/_base/FIELD__base';
import { DTO_GRAPH__edge_autoconnect_info_list } from '../../_graph/DTO_GRAPH__edge_autoconnect_info_list';
import { DTO_GRAPH__instance } from '../../_graph/DTO_GRAPH__instance';
import { INVOCATION__base } from './INVOCATION__base';

declare module './INVOCATION__base' {
  interface INVOCATION__base {
    /*
     * GRAPH ROW
     */

    /*
     * PRIMITIVE
     */

    edges__try_autocreate_from_left_to_right_one_field<T_invocation_to extends INVOCATION__base, T_field_from extends FIELD__base>(
      graph: DTO_GRAPH__instance,
      invocation_to: TYPE__class_constructor<T_invocation_to>,
      field_names_to: (keyof T_invocation_to)[],
      field_from: T_field_from,
      create_only_one_connection?: boolean,
      connect_only_if_no_enabled_edges?: boolean,
    ): void;

    edges__try_autocreate_from_left_to_right<T_invocation extends INVOCATION__base>(
      graph: DTO_GRAPH__instance,
      fields_list: DTO_GRAPH__edge_autoconnect_info_list<T_invocation>,
    ): void;

    edges__try_autocreate_from_right_to_left_one_field<T_invocation_to extends INVOCATION__base, T_field_from extends FIELD__base>(
      graph: DTO_GRAPH__instance,
      invocation_to: TYPE__class_constructor<T_invocation_to>,
      field_names_to: (keyof T_invocation_to)[],
      field_to: T_field_from,
      create_only_one_connection?: boolean,
      connect_only_if_no_enabled_edges?: boolean,
    ): void;

    edges__try_autocreate_from_right_to_left<T_invocation extends INVOCATION__base>(
      graph: DTO_GRAPH__instance,
      fields_list: DTO_GRAPH__edge_autoconnect_info_list<T_invocation>,
    ): void;
  }
}

export function INIT_EXTENSION_INVOCATION__base__edges() {
  // do nothing
}

/*
 * CONNECTABLE
 */

INVOCATION__base.prototype.edges__try_autocreate_from_left_to_right_one_field = function <
  T_invocation_to extends INVOCATION__base,
  T_field_from extends FIELD__base,
>(
  graph: DTO_GRAPH__instance,
  invocation_to: TYPE__class_constructor<T_invocation_to>,
  field_names_to: (keyof T_invocation_to)[],
  field_from: T_field_from,
  create_only_one_connection = true,
  connect_only_if_no_enabled_edges = true,
) {
  let fields_to = new DTO_GRAPH__edge_autoconnect_info_list<T_invocation_to>();
  fields_to.invocation_type = invocation_to;
  fields_to.add(field_from, field_names_to);
  fields_to.create_only_one_connection = create_only_one_connection;
  fields_to.connect_only_if_no_enabled_edges = connect_only_if_no_enabled_edges;

  this.edges__try_autocreate_from_left_to_right(graph, fields_to);
};

INVOCATION__base.prototype.edges__try_autocreate_from_left_to_right = function <T_invocation extends INVOCATION__base>(
  graph: DTO_GRAPH__instance,
  fields_list: DTO_GRAPH__edge_autoconnect_info_list<T_invocation>,
) {
  let nodes = Array.from(graph.nodes_map.values());
  nodes = nodes.filter(node => node instanceof fields_list.invocation_type);

  for (const node of nodes) {
    const fields_map_other = node.fields__get_inputs();
    const connections_to_make: [FIELD__base, FIELD__base][] = [];
    const occupied_other_fields = new Set<string>(); // Prevent matching two "fields_this" to the same "field_other"

    let all_matched = true;

    for (const field_this_info of fields_list.items) {
      let field_this = field_this_info.field_this;

      let found_match = false;

      let fields_other_list = Array.from(fields_map_other.values());
      fields_other_list = fields_other_list.filter(field => field_this_info.field_other_names.includes(field.meta.field_name as keyof T_invocation));

      for (const field_other of fields_other_list) {
        if (!field_other.is_field_is_compatible_to_sync_with(field_this)) continue; // 1. Skip if types are incompatible
        // 2. Skip if we already assigned this specific field_other to another field_this in this node
        if (occupied_other_fields.has(field_other.meta.field_id)) continue;
        // 3. Skip if this field_other already has an incoming edge in the graph
        if (!graph.edges__get_to([field_other]).is_empty() && fields_list.connect_only_if_no_enabled_edges) continue;

        // If we reached here, it's a valid match
        connections_to_make.push([field_this, field_other]);
        occupied_other_fields.add(field_other.meta.field_id);
        found_match = true;

        break;
      }

      if (!found_match) {
        all_matched = false;
        break;
      }
    }

    if (all_matched) {
      for (const [f_this, f_other] of connections_to_make) graph.edge__add_for_fields(f_this, f_other);

      if (fields_list.create_only_one_connection) return; // Successfully linked to one node, exit method
    }
  }
};

INVOCATION__base.prototype.edges__try_autocreate_from_right_to_left_one_field = function <
  T_invocation_to extends INVOCATION__base,
  T_field_from extends FIELD__base,
>(
  graph: DTO_GRAPH__instance,
  invocation_from: TYPE__class_constructor<T_invocation_to>,
  field_names_from: (keyof T_invocation_to)[],
  field_to: T_field_from,
  create_only_one_connection = true,
  connect_only_if_no_enabled_edges = true,
) {
  let fields_from = new DTO_GRAPH__edge_autoconnect_info_list<T_invocation_to>();
  fields_from.invocation_type = invocation_from;
  fields_from.add(field_to, field_names_from);
  fields_from.create_only_one_connection = create_only_one_connection;
  fields_from.connect_only_if_no_enabled_edges = connect_only_if_no_enabled_edges;

  this.edges__try_autocreate_from_right_to_left(graph, fields_from);
};

INVOCATION__base.prototype.edges__try_autocreate_from_right_to_left = function <T_invocation extends INVOCATION__base>(
  graph: DTO_GRAPH__instance,
  fields_list: DTO_GRAPH__edge_autoconnect_info_list<T_invocation>,
) {
  let nodes = Array.from(graph.nodes_map.values());
  nodes = nodes.filter(node => node instanceof fields_list.invocation_type);

  for (const node of nodes) {
    const fields_map_other = node.fields__get_map(true);
    const connections_to_make: [FIELD__base, FIELD__base][] = [];
    const occupied_other_fields = new Set<string>(); // Prevent matching two "fields_this" to the same "field_other"

    let found_each_field_match = true;

    for (const field_this_info of fields_list.items) {
      let field_this = field_this_info.field_this;

      let found_match = false;

      let fields_other_list = Array.from(fields_map_other.values());
      fields_other_list = fields_other_list.filter(field => field_this_info.field_other_names.includes(field.meta.field_name as keyof T_invocation));

      for (const field_other of fields_other_list) {
        if (!field_other.is_field_is_compatible_to_sync_with(field_this)) continue; // 1. Skip if types are incompatible
        // 2. Skip if we already assigned this specific field_other to another field_this in this node
        if (occupied_other_fields.has(field_other.meta.field_id)) continue;
        // 3. Skip if this field_other already has an incoming edge in the graph
        if (!graph.edges__get_from([field_other]).is_empty() && fields_list.connect_only_if_no_enabled_edges) continue;

        // If we reached here, it's a valid match
        connections_to_make.push([field_this, field_other]);
        occupied_other_fields.add(field_other.meta.field_id);
        found_match = true;

        break;
      }

      if (!found_match) {
        found_each_field_match = false;
        break;
      }
    }

    if (found_each_field_match) {
      for (const [f_this, f_other] of connections_to_make) graph.edge__add_for_fields(f_other, f_this);

      if (fields_list.create_only_one_connection) return; // Successfully linked to one node, exit method
    }
  }
};
